import React, { useEffect, useMemo, useState } from 'react'
import '../styles/MainContainer.css'
import { Link } from 'react-router-dom';
import { ROUTES } from '../../util/routes';
import './UpcomingEventsList.css';
import '../popup/EditPopup.css'
import ListHeader from '../list/ListHeader';
import ListItem from '../list/ListItem';
import ListTextItem from '../list/ListTextItem';
import EventStatus from '../status/EventStatus';
import ListSymbols from '../list/ListSymbols';
import ListSymbol from '../list/ListSymbol';
import EventContextMenu from '../event/EventContextMenu';
import CustomDropdown from '../input/CustomDropdown';
import Icon from '../home/Icon';
import Tooltip from '../tooltip/Tooltip';
import OfflineBanner from '../shell/OfflineBanner';
import AddOnChips from '../add-on/AddOnChips';
import { addOnCounterItems } from '../../util/addOns';
import { eventRules } from '../../util/eventRules';
import { releaseSummary } from '../../util/participantGroups';
import useParticipantGroups from '../../hooks/useParticipantGroups';

const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function UpcomingEventsList({posts, setPosts, locationsApi}) {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0); // 0 = Alle
  // Eine geteilte Gruppenliste für alle Zeilen (Freigabe-Text im Status-Tooltip) — kein Request je Zeile.
  const { byId: groupsById } = useParticipantGroups();

  const years = useMemo(() => {
    return [...new Set(posts.map(p => new Date(p.date).getFullYear()))].sort((a, b) => b - a);
  }, [posts]);

  useEffect(() => {
    if (years.length > 0 && selectedYear == null) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  const months = useMemo(() => {
    if (selectedYear == null) return [];
    return [...new Set(
      posts
        .filter(p => new Date(p.date).getFullYear() === selectedYear)
        .map(p => new Date(p.date).getMonth() + 1)
    )].sort((a, b) => a - b);
  }, [posts, selectedYear]);

  const displayPosts = useMemo(() => {
    let result = posts;
    if (selectedYear != null) result = result.filter(p => new Date(p.date).getFullYear() === selectedYear);
    if (selectedMonth !== 0) result = result.filter(p => new Date(p.date).getMonth() + 1 === selectedMonth);
    return result;
  }, [posts, selectedYear, selectedMonth]);

  const handleYearChange = (e) => {
    setSelectedYear(Number(e.target.value));
    setSelectedMonth(0);
  };

  const visualType = () => 'normal';

  return (
    <React.Fragment>
      <main className={"eventColumnMain"}>
        <OfflineBanner />
        {years.length > 0 && (
          <div className={"upcomingEventsFilterBar"}>
            <div className={"upcomingEventsDropdown"}>
              <CustomDropdown
                icon="calendar_month"
                value={String(selectedYear ?? '')}
                onChange={handleYearChange}
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </CustomDropdown>
            </div>
            <div className={"upcomingEventsDropdown"}>
              <CustomDropdown
                icon="date_range"
                value={String(selectedMonth)}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                <option value={0}>Alle</option>
                {months.map(m => <option key={m} value={m}>{MONTH_LABELS[m - 1]}</option>)}
              </CustomDropdown>
            </div>
          </div>
        )}
        <ListHeader gridTemplateColumns={'2fr 1.2fr 0.6fr 1.2fr'} className="upcomingCard">
          <ListTextItem isHeader>Datum</ListTextItem>
          <ListTextItem isHeader>Teilnehmer</ListTextItem>
          <ListTextItem isHeader>Zusätze</ListTextItem>
          <ListTextItem isHeader>Status</ListTextItem>
        </ListHeader>
        {displayPosts.map((post, index) => {
          const rules = eventRules(post);
          // Counter chips of the process's sign-up add-ons (EventAddOnSummary: used/max, warning when exhausted).
          const addOnItems = addOnCounterItems(post.addOns);
          // Layout class of the counter cell (child 3): one chip stays in the row on wide screens, several chips
          // (and any chip below 1000px) get their own row — see UpcomingEventsList.css.
          const addOnLayout = addOnItems.length > 1 ? ' hasAddOnRow' : addOnItems.length === 1 ? ' hasAddOn' : '';
          return (
            <Link to={ROUTES.event(post.id, post.date)} key={post.id} className="eventListLink">
              <ListItem
                key={post.id}
                identifier={post.id}
                gridTemplateColumns={'2fr 1.2fr 0.6fr 1.2fr'}
                memo={post.memo}
                contextMenuIdentifier='upcomingevents'
                index={index}
                type={visualType(post)}
                className={`upcomingCard${addOnLayout}`}
              >
                <ListTextItem type="primary">
                  <div className={"eventDateCell"}>
                    <span className="eventDatePrimary">{`${new Date(post.date).getDate()}. ${MONTH_LABELS[new Date(post.date).getMonth()]}`}</span>
                    <span className="eventDateSecondary">{new Date(post.date).getFullYear()}</span>
                  </div>
                </ListTextItem>
                <ListTextItem type="meta">
                  <div className={"participantsContainer"}>
                    <Icon icon="person" className="upcomingMetaIcon" />
                    <p>{post.confirmedCount}{post.maxPlayers ? ` / ${post.maxPlayers}` : ''}{post.waitlistCount > 0 && <span className={"waitlistCount"}> +{post.waitlistCount}</span>}</p>
                  </div>
                </ListTextItem>
                {/* Zusätze-Zelle bleibt immer Kind 3 (positionsbasiertes Grid in UpcomingEventsList.css);
                    ohne Anmelde-Zusätze im Prozess nur „–". „/N" nur bei Limit (0 = unbegrenzt). */}
                <ListTextItem type="meta">
                  {addOnItems.length > 0 ? (
                    <AddOnChips items={addOnItems} compact className="upcomingAddOnChips" ariaLabel={'Anmelde-Zusätze'} />
                  ) : (
                    <Tooltip text={'Keine Zusätze'}>
                      <div className="upcomingAddOnCell isOff" aria-label={'Keine Zusätze'}>–</div>
                    </Tooltip>
                  )}
                </ListTextItem>
                <EventStatus statusId={post.state} event={post} releasedFor={releaseSummary(post.participantGroupSettings, groupsById)} compact />
                <ListSymbols>
                  <ListSymbol icon={"note_stack"} visible={!!post.memo} type={0} />
                  <ListSymbol icon={"lock"}       visible={rules.linkSignup} type={1}
                    tooltip={`${rules.eventLabel} – Anmeldung über Einladungslink`} />
                  <ListSymbol icon={"lock_person"} visible={rules.linkSignup && !!post.signupClosed} type={2}
                    tooltip={'Anmeldung geschlossen – keine neuen Anmeldungen über Einladungslink und persönliche Links'} />
                  <ListSymbol icon={"groups"}     visible={rules.neverPublic && !rules.linkSignup} type={2} tooltip={rules.eventLabel} />
                </ListSymbols>
              </ListItem>
            </Link>
          );
        })}
      </main>
      <EventContextMenu posts={posts} setPosts={setPosts} type='upcomingevents' locationsApi={locationsApi} />
    </React.Fragment>
  )
}

export default UpcomingEventsList
